import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories } from '@/mocks/comics';
import type { ComicItem } from '@/mocks/types';

const siteUrl = 'https://tianbaolaowu.cn';

function getBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'hot' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryName = activeCategory === 'all' ? '全部漫画' : (currentCategory?.name || '全部漫画');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [activeCategory, sortBy]);

  let filteredComics: ComicItem[] = activeCategory === 'all'
    ? [...comics]
    : comics.filter((c) => c.category === activeCategory);

  if (sortBy === 'latest') {
    filteredComics.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
  } else if (sortBy === 'hot') {
    filteredComics.sort((a, b) => b.views - a.views);
  } else {
    filteredComics.sort((a, b) => b.rating - a.rating);
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/love' },
    { label: categoryName },
  ];

  const breadcrumbSchemaItems = [
    { name: '首页', url: siteUrl },
    { name: '分类浏览', url: `${siteUrl}/category/love` },
    { name: categoryName, url: `${siteUrl}/category/${activeCategory}` },
  ];

  const seoData = {
    title: `${categoryName}韩国漫画_热门${categoryName}韩漫推荐_韩漫漫画网`,
    description: `韩漫漫画网${categoryName}分类为您推荐最新最全的${categoryName}类韩国漫画，免费下拉式阅读，高清在线观看，每日更新，丰富您的漫画阅读选择。`,
    keywords: `${categoryName}漫画,${categoryName}韩漫,韩国漫画,热门推荐,免费阅读,在线阅读`,
    canonical: `${siteUrl}/category/${activeCategory}`,
    ogType: 'website',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: `${categoryName}韩国漫画`,
        url: `${siteUrl}/category/${activeCategory}`,
      },
      getBreadcrumbSchema(breadcrumbSchemaItems),
    ],
  };

  return (
    <>
      <SEOHead data={seoData} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex items-center gap-3 mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {categoryName}漫画
              </h1>
              <span className="text-sm text-foreground-500">共 {filteredComics.length} 部</span>
            </div>

            {/* Category Tabs */}
            <div className="mb-6 overflow-x-auto">
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit min-w-max">
                <Link
                  to="/category/all"
                  onClick={() => setActiveCategory('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === 'all'
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  全部
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              {([
                { key: 'latest', label: '最新更新' },
                { key: 'hot', label: '最热门' },
                { key: 'rating', label: '最高评分' },
              ] as const).map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Comic Grid */}
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                <p className="mt-4 text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部漫画
                </Link>
              </div>
            )}

            {/* Pagination Info */}
            <div className="mt-10 text-center">
              <p className="text-sm text-foreground-500">
                当前显示 {filteredComics.length} 部{categoryName}漫画
              </p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}